/**
 * Step 2: Landlord Contact Information
 * Auto-populated from manager profile and property address, but all fields are overridable.
 * Collects: full name, mailing address (street, city, state, zip), email, phone, contact methods.
 */

import type { WizardState, LandlordContact } from '../../../types/lease-generation';
import { RequiredMark } from '../RequiredMark';

interface Step2Props {
  state: WizardState;
  updateLandlordContact: (partial: Partial<LandlordContact>) => void;
}

const CONTACT_METHOD_OPTIONS: Array<{ value: LandlordContact['contactMethods'][number]; label: string }> = [
  { value: 'mail', label: 'Mail' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'in-app-chat', label: 'In-app Chat' },
];

export function Step2LandlordContact({ state, updateLandlordContact }: Step2Props) {
  const contact = state.landlordContact;

  const toggleContactMethod = (method: LandlordContact['contactMethods'][number]) => {
    const current = contact.contactMethods;
    const updated = current.includes(method)
      ? current.filter(m => m !== method)
      : [...current, method];
    updateLandlordContact({ contactMethods: updated });
  };

  return (
    <div className="flex flex-col gap-6" data-testid="step-landlord-contact">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Landlord Contact Information</h3>
        <p className="text-sm text-muted-foreground">
          Pre-filled from your profile and property address. All fields are overridable.
          This information appears in the lease document.
        </p>
      </div>

      {/* Full Name */}
      <div>
        <label htmlFor="landlord-full-name" className="block text-sm font-medium text-foreground mb-1">
          Full Legal Name <RequiredMark />
        </label>
        <input
          id="landlord-full-name"
          type="text"
          data-testid="landlord-full-name-input"
          value={contact.fullName}
          onChange={e => updateLandlordContact({ fullName: e.target.value })}
          className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
          placeholder="Your full legal name"
        />
      </div>

      {/* Mailing Address */}
      <fieldset>
        <legend className="block text-sm font-medium text-foreground mb-2">Mailing Address <RequiredMark /></legend>
        <div className="flex flex-col gap-3">
          <input
            type="text"
            data-testid="landlord-mailing-address-input"
            value={contact.mailingAddress}
            onChange={e => updateLandlordContact({ mailingAddress: e.target.value })}
            className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
            placeholder="Street address"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <input
              type="text"
              data-testid="landlord-city-input"
              value={contact.city}
              onChange={e => updateLandlordContact({ city: e.target.value })}
              className="px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
              placeholder="City"
            />
            <input
              type="text"
              data-testid="landlord-state-input"
              value={contact.state}
              onChange={e => updateLandlordContact({ state: e.target.value.toUpperCase().slice(0, 2) })}
              className="px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
              placeholder="State"
              maxLength={2}
            />
            <input
              type="text"
              data-testid="landlord-zip-input"
              value={contact.zipCode}
              onChange={e => updateLandlordContact({ zipCode: e.target.value })}
              className="px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px] col-span-2 sm:col-span-1"
              placeholder="ZIP code"
            />
          </div>
        </div>
      </fieldset>

      {/* Email */}
      <div>
        <label htmlFor="landlord-email" className="block text-sm font-medium text-foreground mb-1">
          Contact Email <RequiredMark />
        </label>
        <input
          id="landlord-email"
          type="email"
          data-testid="landlord-email-input"
          value={contact.email}
          onChange={e => updateLandlordContact({ email: e.target.value })}
          className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
          placeholder="your@email.com"
        />
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="landlord-phone" className="block text-sm font-medium text-foreground mb-1">
          Phone Number
        </label>
        <input
          id="landlord-phone"
          type="tel"
          data-testid="landlord-phone-input"
          value={contact.phone}
          onChange={e => updateLandlordContact({ phone: e.target.value })}
          className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
          placeholder="(555) 555-5555"
        />
      </div>

      {/* Contact Methods */}
      <fieldset>
        <legend className="block text-sm font-medium text-foreground mb-2">
          Preferred Contact Methods
          <span className="ml-1 text-xs font-normal text-muted-foreground">(select at least one)</span>
        </legend>
        <div className="flex flex-wrap gap-3">
          {CONTACT_METHOD_OPTIONS.map(option => {
            const isSelected = contact.contactMethods.includes(option.value);
            return (
              <label
                key={option.value}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors min-h-[44px] ${
                  isSelected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-card text-foreground hover:bg-accent'
                }`}
              >
                <input
                  type="checkbox"
                  data-testid={`contact-method-${option.value}`}
                  checked={isSelected}
                  onChange={() => toggleContactMethod(option.value)}
                  className="sr-only"
                />
                <span className="text-sm font-medium">{option.label}</span>
              </label>
            );
          })}
        </div>
        {contact.contactMethods.length === 0 && (
          <p className="mt-2 text-xs text-destructive" data-testid="contact-methods-error">
            At least one contact method is required.
          </p>
        )}
      </fieldset>
    </div>
  );
}
